// How a quote is entered and how it reads (TX-US-0040). The codes are the API's; the words are
// ours. Pure: the form and the list are in CounterpartyQuotes.jsx.

// What the price is for. Mirrors QuoteBasis in trex/counterparties/quotes/models.py.
export const QUOTE_BASES = [
  { code: 'MT', label: 'per MT' },
  { code: 'M3', label: 'per m³' },
  { code: 'LITRE', label: 'per litre' },
  { code: 'TRUCK', label: 'per truck' },
  { code: 'TRIP', label: 'per trip' },
  { code: 'LUMP_SUM', label: 'lump sum' },
];

export const basisLabel = (code) => QUOTE_BASES.find((basis) => basis.code === code)?.label ?? code;

// Mirrors MAX_SERVICE_LENGTH and MAX_CONDITIONS_LENGTH in the same module.
export const MAX_SERVICE_LENGTH = 200;
export const MAX_CONDITIONS_LENGTH = 4000;

// A quote is for a product from the list or for a service in words, and the form shows only that
// field, so the two cannot both be filled in.
export const FOR_PRODUCT = 'product';
export const FOR_SERVICE = 'service';

const SELLS_PRODUCTS = ['SUPPLIER', 'CUSTOMER'];
const SELLS_SERVICES = ['LOGISTICS', 'SERVICE_PROVIDER'];

// What a counterparty quotes for follows from what it is: a supplier or a customer quotes a
// product, a logistics company or a service provider a service. A counterparty that is both, or
// neither yet, gives no answer - the form then asks.
export function quotedForOf(categories = []) {
  const products = categories.some((c) => SELLS_PRODUCTS.includes(c));
  const services = categories.some((c) => SELLS_SERVICES.includes(c));
  if (products === services) return null;
  return products ? FOR_PRODUCT : FOR_SERVICE;
}

export const emptyQuote = (today, quotedFor = FOR_SERVICE) => ({
  quotedOn: today,
  quotedFor,
  product: '',
  service: '',
  amount: '',
  currency: 'USD',
  per: 'MT',
  conditions: '',
});

// A positive amount as typed, with at most one decimal point and no exponent: the API takes the
// string as it is and never sees a float.
const AMOUNT = /^\d+(\.\d+)?$/;

// What still stops the quote from being saved, in the order the form asks for it; empty when
// it can be.
export function missing(draft, today) {
  const out = [];
  if (!draft.quotedOn) out.push('the date it was quoted');
  else if (draft.quotedOn > today) out.push('a date that is not in the future');
  if (draft.quotedFor === FOR_PRODUCT && !draft.product) out.push('the product');
  if (draft.quotedFor === FOR_SERVICE && !draft.service.trim()) out.push('the service');
  const amount = draft.amount.trim();
  if (!AMOUNT.test(amount) || Number(amount) === 0) out.push('a price above zero');
  if (!draft.conditions.trim()) out.push('the conditions');
  return out;
}

export function quoteBody(draft) {
  const forProduct = draft.quotedFor === FOR_PRODUCT;
  return {
    quoted_on: draft.quotedOn,
    product: forProduct ? draft.product : null,
    service: forProduct ? null : draft.service.trim(),
    price: { amount: draft.amount.trim(), currency: draft.currency },
    per: draft.per,
    conditions: draft.conditions.trim(),
  };
}

// The product's label from the list, the service as it was written.
export const subjectOf = (quote, productLabel = (code) => code) => (
  quote.product ? productLabel(quote.product) : quote.service
);

// "USD 38.00 per MT", "USD 1,200.00 lump sum".
export function priceText(quote) {
  const amount = Number(quote.price.amount).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
  return `${quote.price.currency} ${amount} ${basisLabel(quote.per)}`;
}

// The date a quote was given, as the list shows it: 14 Oct 2026. A date has no time zone, so it
// is formatted as the day it names, never shifted into the reader's.
export function quoteDate(isoDay) {
  const [year, month, day] = isoDay.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

// Today in the reader's own calendar, as a date input wants it.
export function localToday(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
